import React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import State from '~Types/state';
import * as actions from '../../../../actions';
import _ from 'lodash';
import { User } from '~Types';
import { useButton, useDispatch, useI18n, useLicenses, useSelector } from '~/Hooks';
import { VenueData_venue_floorPlans } from '../../__queries__';
import NavigationItem from '../NavigationItem';
import Swap from '~Assets/icons/swap.svg';
import { useHistory } from 'react-router';
import { Button } from '~Styles';
import { useBookings } from '~Providers/BookingsProvider';
import { sortFloorPlans } from '~Utils/sortFloorPlans';

const Wrapper = styled.div`
  background-color: var(--colorBlueGray1100);
  width: 176px;
  display: flex;
  flex-flow: column nowrap;
`;

const ActionsWrapper = styled.div`
  margin: auto 16px 32px;
`;

const StaffName = styled.div`
  color: var(--colorWhite);
  margin-top: 1px;
  font-size: 14px;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const Icon = styled.object`
  width: 24px;
  height: 24px;
  pointer-events: none;
`;

const LogoutButton = styled(Button)`
  background-color: transparent;
  color: var(--colorBlueGray400);
  border: 1px solid var(--colorBlueGray800);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 2px 8px;
  height: 50px;
  margin-bottom: 16px;
`;

const PosButton = styled(Button)`
  background-color: transparent;
  color: var(--colorBlueGray400);
  border: 1px solid var(--colorBlueGray800);
  display: flex;
  gap: 8px;
  width: 100%;
  height: 50px;
`;

interface Props {
  selectedNav: string;
  changeNav: (nav: string) => void;
  setToken: (token: string | null) => void;
  deviceId: string;
  setUser: (user: User | null) => void;
  floorPlans: VenueData_venue_floorPlans[];
}

const Navigation: React.FunctionComponent<Props> = ({ selectedNav, changeNav, floorPlans }) => {
  const staff = useSelector((state) => state.staff);
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const history = useHistory();
  const posMode = useSelector((state) => state.lastUsedPosMode);
  const licenses = useLicenses();
  const bookings = useBookings();

  const staffCodes = useSelector((state) => state.companyStaff?.staffs ?? []);
  const hasStaffCodes = staffCodes.length > 0;

  const logOutButton = useButton({
    onClick: () => dispatch(actions.staffLogout()),
  });

  const logInButton = useButton({
    onClick: () => dispatch(actions.staffLogout()),
  });

  const goToPosButton = useButton({
    onClick: () => history.push(posMode === 'SINGLE' ? 'pos' : 'multi-pos'),
  });

  const sortedFloorPlans = sortFloorPlans(floorPlans);

  return (
    <Wrapper>
      <NavigationItem selectedNav={selectedNav} changeNav={changeNav} nav={'Grid'}>
        {i18n('TM.Grid')}
      </NavigationItem>

      <NavigationItem selectedNav={selectedNav} changeNav={changeNav} nav={'Open'}>
        {i18n('TM.OpenTables')}
      </NavigationItem>

      {sortedFloorPlans.map((floorPlan: VenueData_venue_floorPlans) => (
        <NavigationItem
          key={floorPlan.id}
          selectedNav={selectedNav}
          changeNav={changeNav}
          nav={floorPlan.id}>
          {floorPlan.name}
        </NavigationItem>
      ))}

      {bookings.enabled && (
        <NavigationItem
          selectedNav={selectedNav}
          changeNav={changeNav}
          nav={'Bookings'}
          incomingBookingsCount={bookings.incoming.length}>
          {i18n('Bookings')}
        </NavigationItem>
      )}

      <ActionsWrapper>
        {hasStaffCodes && (
          <>
            {staff ? (
              <LogoutButton {...logOutButton}>
                {i18n('General.Logout')}
                <StaffName>{staff.name}</StaffName>
              </LogoutButton>
            ) : (
              <LogoutButton {...logInButton}>{i18n('Menu.Login')}</LogoutButton>
            )}
          </>
        )}
        {licenses.hasQSR && (
          <PosButton {...goToPosButton}>
            <Icon data={Swap} /> POS
          </PosButton>
        )}
      </ActionsWrapper>
    </Wrapper>
  );
};

const mapStateToProps = (state: State) => ({
  deviceId: state.deviceId,
});

const mapDispatchToProps = {
  setUser: actions.setUser,
  setToken: actions.setToken,
};

export default _.flowRight(connect(mapStateToProps, mapDispatchToProps))(Navigation);
